import { LogOut } from "lucide-react";
import { useEffect, useRef } from "react";

import { useNavItems } from "../lib/navPrefs";
import { useSignOut } from "../lib/useSignOut";
import type { NavKey } from "./nav";

interface Props {
  active: NavKey;
  onChange: (key: NavKey) => void;
}

/**
 * タブの切り替え。スマホ・タブレットでは画面下部の固定バー、PC(xl以上)では左のサイドバー。
 *
 * 並び・表示・名前・アイコンはユーザーごとの設定(nav_prefs)を重ねて出す。
 * 下部バーは枠数を決めず、幅に収まらないぶんは横スクロールで送る
 * (1タブの幅は最小56pxを保証し、余れば等分に広がる)。
 * `fixed` はビューポート基準に位置するため、body のセーフエリア用パディングが
 * 効かない。下部バーは自分で下側を空ける。
 */
export default function BottomNav({ active, onChange }: Props) {
  const visible = useNavItems().filter((item) => !item.hidden);
  // 設定はメニュー編集への入口なので、スクロール列から外して右端に固定で置く
  const scrollItems = visible.filter((item) => item.key !== "settings");
  const settings = visible.find((item) => item.key === "settings");
  const SettingsIcon = settings?.icon;
  const signOut = useSignOut();

  // 横スクロールで画面の外にいる選択中タブを、初回表示と切り替えのたびに見える位置へ寄せる
  const activeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active]);

  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-[var(--color-line)] bg-[var(--color-bg)] pb-[env(safe-area-inset-bottom)] xl:hidden">
        <div className="mx-auto flex max-w-[var(--bar-w)] md:max-w-[var(--bar-w-md)]">
          <ul className="scrollbar-none flex flex-1 overflow-x-auto">
            {scrollItems.map(({ key, label, icon: Icon }) => {
              const isActive = key === active;
              return (
                <li key={key} className="min-w-14 flex-1">
                  <button
                    ref={isActive ? activeRef : undefined}
                    type="button"
                    onClick={() => {
                      onChange(key);
                    }}
                    aria-current={isActive ? "page" : undefined}
                    // ラベルを出さないぶん、名前は aria-label に残す
                    aria-label={label}
                    className={`flex w-full items-center justify-center py-3 transition-colors ${
                      isActive ? "text-[var(--color-accent)]" : "text-[var(--color-sub)]"
                    }`}
                  >
                    <Icon className="size-6" aria-hidden />
                  </button>
                </li>
              );
            })}
            {/* サインアウトはタブ列の一番右。固定はせず、スクロールと一緒に流れる */}
            <li className="min-w-14 flex-1">
              <button
                type="button"
                onClick={() => void signOut()}
                aria-label="サインアウト"
                className="flex w-full items-center justify-center py-3 text-[var(--color-sub)] transition-colors"
              >
                <LogOut className="size-6" aria-hidden />
              </button>
            </li>
          </ul>
          {settings !== undefined && SettingsIcon !== undefined && (
            <button
              type="button"
              onClick={() => {
                onChange(settings.key);
              }}
              aria-current={active === settings.key ? "page" : undefined}
              aria-label={settings.label}
              className={`flex w-14 shrink-0 items-center justify-center border-l border-[var(--color-line)] py-3 transition-colors ${
                active === settings.key ? "text-[var(--color-accent)]" : "text-[var(--color-sub)]"
              }`}
            >
              <SettingsIcon className="size-6" aria-hidden />
            </button>
          )}
        </div>
      </nav>

      {/* サイドバーは PC(xl〜)だけ。タブレットは下部バーを使う */}
      <nav className="fixed inset-y-0 left-0 z-30 hidden w-[var(--sidebar-w)] flex-col border-r border-[var(--color-line)] bg-[var(--color-bg)] xl:flex">
        <span className="px-6 pt-6 pb-4 text-lg font-semibold text-[var(--color-accent)]">
          Yuu&amp;mi
        </span>
        {/* タブが縦に収まらない環境でも、サインアウトを画面外へ押し出さない */}
        <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3">
          {visible.map(({ key, label, icon: Icon }) => {
            const isActive = key === active;
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(key);
                  }}
                  aria-current={isActive ? "page" : undefined}
                  // 選択中はフラットな淡いテーマカラーの塗り+左端の縦バー。
                  // カード(白+影)にしないのは、面より「印」で今いる場所を示すため
                  className={`relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "bg-[color-mix(in_srgb,var(--color-accent)_10%,transparent)] font-medium text-[var(--color-accent)]"
                      : "text-[var(--color-sub)] hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)] hover:text-[var(--color-ink)]"
                  }`}
                >
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute inset-y-1.5 left-0 w-[3px] rounded-full bg-[var(--color-accent)]"
                    />
                  )}
                  <Icon className="size-5 shrink-0" aria-hidden />
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
        {/* PC はヘッダーのメニューを無くしたので、サインアウトの置き場はここ。
            タブレット以下ではヘッダーのメニューが持つ */}
        <button
          type="button"
          onClick={() => void signOut()}
          className="mx-3 mb-3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-[var(--color-sub)] transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)] hover:text-[var(--color-ink)]"
        >
          <LogOut className="size-4" aria-hidden />
          サインアウト
        </button>
      </nav>
    </>
  );
}
